import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { MessageSquare, Send, ArrowLeft } from 'lucide-react';

export default function Messages() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const isResearcher = user?.role === 'researcher';
  const primaryColor = isResearcher ? '#FF6B35' : '#0D7377';
  
  // Check if there's a "to" param for starting new conversation
  const toUserId = searchParams.get('to');
  
  useEffect(() => {
    fetchConversations();
  }, []);
  
  useEffect(() => {
    if (toUserId && !selectedPartner) {
      // Start new conversation
      fetchPartnerInfo(toUserId);
    }
  }, [toUserId]);
  
  useEffect(() => {
    if (selectedPartner) {
      fetchMessages(selectedPartner.user_id);
    }
  }, [selectedPartner]);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages');
      setConversations(response.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPartnerInfo = async (userId) => {
    try {
      const response = await api.get(`/profiles/${userId}`);
      setSelectedPartner(response.data.user);
    } catch (error) {
      console.error('Error fetching partner info:', error);
    }
  };
  
  const fetchMessages = async (partnerId) => {
    try {
      const response = await api.get(`/messages/${partnerId}`);
      setMessages(response.data.messages || []);
      // Update conversations to mark as read
      setConversations(prev => prev.map(conv => 
        conv.partner.user_id === partnerId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };
  
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;
    
    setSending(true);
    try {
      const response = await api.post('/messages', {
        receiver_id: selectedPartner.user_id,
        content: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Update conversations list
      const existingConv = conversations.find(c => c.partner.user_id === selectedPartner.user_id);
      if (!existingConv) {
        setConversations(prev => [{
          partner: selectedPartner,
          messages: [response.data],
          unread_count: 0
        }, ...prev]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const selectConversation = (conv) => {
    setSelectedPartner(conv.partner);
  };
  
  if (loading) {
    return (
      <DashboardLayout title="Messages">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColor }}></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="Messages">
      <div className="bg-white border border-neutral-200 h-[calc(100vh-200px)] min-h-[500px] flex">
        {/* Conversations List */}
        <div className={`w-80 border-r border-neutral-200 flex-shrink-0 ${selectedPartner ? 'hidden md:block' : ''}`}>
          <div className="p-4 border-b border-neutral-200">
            <h3 className="font-semibold" style={{ fontFamily: 'Outfit' }}>Conversations</h3>
          </div>
          
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-neutral-500">
              <MessageSquare className="mx-auto mb-2 text-neutral-300" size={32} />
              <p className="text-sm">No conversations yet</p>
            </div>
          ) : (
            <div className="overflow-y-auto h-[calc(100%-60px)]">
              {conversations.map((conv) => (
                <button
                  key={conv.partner.user_id}
                  data-testid={`conversation-${conv.partner.user_id}`}
                  onClick={() => selectConversation(conv)}
                  className={`w-full p-4 text-left border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${
                    selectedPartner?.user_id === conv.partner.user_id ? 'bg-neutral-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {conv.partner.picture ? (
                      <img 
                        src={conv.partner.picture} 
                        alt={conv.partner.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {conv.partner.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{conv.partner.name}</p>
                        {conv.unread_count > 0 && (
                          <span 
                            className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-500 truncate">
                        {conv.messages[0]?.content || 'No messages'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedPartner ? 'hidden md:flex' : ''}`}>
          {selectedPartner ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-neutral-200 flex items-center gap-3">
                <button
                  onClick={() => setSelectedPartner(null)}
                  className="md:hidden p-1 hover:bg-neutral-100 rounded"
                >
                  <ArrowLeft size={20} />
                </button>
                {selectedPartner.picture ? (
                  <img 
                    src={selectedPartner.picture} 
                    alt={selectedPartner.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {selectedPartner.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-semibold">{selectedPartner.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-sm ${
                    selectedPartner.role === 'researcher' ? 'badge-researcher' : 'badge-client'
                  }`}>
                    {selectedPartner.role}
                  </span>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg) => {
                  const isMine = msg.sender_id === user.user_id;
                  return (
                    <div
                      key={msg.message_id}
                      data-testid={`message-${msg.message_id}`}
                      className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] px-4 py-2 ${
                          isMine
                            ? 'text-white'
                            : 'bg-neutral-100 text-neutral-900'
                        }`}
                        style={isMine ? { backgroundColor: primaryColor } : {}}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMine ? 'text-white/70' : 'text-neutral-500'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Message Input */}
              <form onSubmit={sendMessage} className="p-4 border-t border-neutral-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    data-testid="message-input"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1 px-4 py-2 border border-neutral-200 focus:border-neutral-400 outline-none"
                  />
                  <button
                    type="submit"
                    data-testid="send-message-btn"
                    disabled={sending || !newMessage.trim()}
                    className="px-4 py-2 text-white disabled:opacity-50 transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {sending ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send size={20} />
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-neutral-500">
              <div className="text-center">
                <MessageSquare className="mx-auto mb-4 text-neutral-300" size={48} />
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
